import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  Alert,
  Button,
  ButtonDropdown,
  Spinner,
  Grid,
  Box,
  Icon,
  Select,
  Textarea,
  Input
} from '@amzn/awsui-components-core';
import { faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmployeeSearchBar } from '../components/attendees/EmployeeSearchBar';
import { AttendeeCard } from '../components/attendees/AttendeeCard';
import {
  MeetingFormData,
  AttendeePriority,
  AttendeeListProps,
  DocumentUploadProps,
  SharedMeetingViewProps,
  RecentMeeting,
  ResolvedMatch,
  SortOption
} from '../interfaces/interface';
import { UploadTemplateContainer } from './modals/UploadTemplateModal';
import { ImportFromMeetingModal } from './modals/ImportFromMeetingModal';
import { PasteListModal } from './modals/PasteListModal';
import { CopyDropdown } from '../components/copy/CopyDropdown';
import { loadAttendeesInBatches } from '../utils/attendeeUtils';
import { EmployeeService } from '../services/EmployeeService';
import { BrassService, BrassGroupType } from 'src/services/BrassService';
import { nameResolver } from '../utils/NameResolver';
import { MEETING_CONSTANTS, PASTE_ATTENDEES_PLACEHOLDER } from '../constants/EAHuddleConstants';
import { v4 as uuidv4 } from 'uuid';
import { logger } from 'src/logger';
import './AddAttendeesContainer.scss';

interface AddAttendeesContainerProps {
  formData: MeetingFormData;
  onInputChange: (field: keyof MeetingFormData, value: string) => void;
  attendees: AttendeeListProps;
  document: DocumentUploadProps;
  sharedMeeting: SharedMeetingViewProps;
  executiveAssistants: Array<{ name: string; email: string }>;
  onMultiBadgerClick: () => void;
  onDownloadCsv: () => void;
  initialExpanded?: boolean;
  recentMeetings: RecentMeeting[];
  initialShowCopyFromMeeting?: boolean;
  initialShowUpload?: boolean;
}

const SORT_LABELS: Record<SortOption, string> = { priority: 'Priority', level: 'Level', name: 'Name' };

export const AddAttendeesContainer: React.FC<AddAttendeesContainerProps> = ({
  formData,
  onInputChange,
  attendees,
  document: doc,
  sharedMeeting,
  executiveAssistants,
  onMultiBadgerClick,
  onDownloadCsv,
  initialExpanded = true,
  recentMeetings,
  initialShowCopyFromMeeting = false,
  initialShowUpload = false
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [showUploadLocal, setShowUploadLocal] = useState(initialShowUpload);
  const [showCopyFromMeeting, setShowCopyFromMeeting] = useState(initialShowCopyFromMeeting);
  const [showPasteList, setShowPasteList] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [isPasteLoading, setIsPasteLoading] = useState(false);
  const [pasteError, setPasteError] = useState('');
  const [groupMode, setGroupMode] = useState(false);
  const [groupText, setGroupText] = useState('');
  const [isGroupLoading, setIsGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState('');

  const {
    resolvedAttendees,
    existingEmails,
    duplicateEmails,
    onAddAttendee,
    onAttendeeUpdate,
    onAttendeeRemove,
    onToggleFavorite,
    onAddProxy,
    onResort,
    onEaOverride,
    onEaRevert,
    appendAttendees,
    sortBy,
    onSortByChange
  } = attendees;
  const { isProcessingDocument, documentError, onFileSelected, onFormattedContentExtracted, showUploadModal, onHideUploadModal } = doc;
  const { isLoadingSharedMeeting, sharedMeetingProgress, isReadOnly, onSaveMeeting, isSaving } = sharedMeeting;

  const resolvePastedNames = useCallback(
    async (names: string[], priority: AttendeePriority) => {
      const unresolved: ResolvedMatch[] = [];
      for (let i = 0; i < names.length; i += MEETING_CONSTANTS.ATTENDEE_BATCH_SIZE) {
        const batch = names.slice(i, i + MEETING_CONSTANTS.ATTENDEE_BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (rawName) => {
            try {
              const name = rawName.includes('@') ? rawName.split('@')[0] : rawName;
              const aliasResult = await EmployeeService.getEmployeeByLogin(name);
              if (aliasResult) return { name: rawName, employees: [aliasResult] };
              const searchResults = await EmployeeService.searchEmployees(name, 15);
              return { name: rawName, employees: searchResults };
            } catch (error) {
              logger.error('Failed to resolve pasted attendee', { name: rawName, error });
              return { name: rawName, employees: [] };
            }
          })
        );
        const addPromises: Promise<void>[] = [];
        for (const result of batchResults) {
          const matches = nameResolver.findMatchesWithEmployeeData(result.name, result.employees);
          if (matches.selectedEmployee) {
            addPromises.push(Promise.resolve(onAddAttendee(matches.selectedEmployee, priority)));
          } else {
            unresolved.push({
              id: `paste-${uuidv4()}`,
              inputName: result.name,
              matches: matches.matches,
              selectedEmployee: null,
              selectedExecutiveAssistant: null,
              status: matches.status,
              priority,
              resolvedEmail: ''
            });
          }
        }
        await Promise.all(addPromises);
        if (i + MEETING_CONSTANTS.ATTENDEE_BATCH_SIZE < names.length) {
          await new Promise((r) => setTimeout(r, MEETING_CONSTANTS.BATCH_DELAY_MS));
        }
      }
      if (unresolved.length > 0) appendAttendees(unresolved);
    },
    [onAddAttendee, appendAttendees]
  );

  const handleInlinePasteSubmit = useCallback(
    async (priority: AttendeePriority) => {
      const names = pasteText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (names.length === 0) return;
      setPasteError('');
      setIsPasteLoading(true);
      try {
        await resolvePastedNames(names, priority);
        setPasteText('');
        setPasteMode(false);
      } catch (error) {
        logger.error('Inline paste failed', { error });
        setPasteError('Failed to resolve attendees. Please try again.');
      } finally {
        setIsPasteLoading(false);
      }
    },
    [pasteText, resolvePastedNames]
  );

  const handleAddGroup = useCallback(
    async (priority: AttendeePriority) => {
      const groupId = groupText.trim();
      if (!groupId) return;
      if (existingEmails.has(`${groupId}@amazon.com`)) return;
      setGroupError('');
      setIsGroupLoading(true);
      try {
        // Validate group exists by fetching its members
        const response = await BrassService.listActorGroupMembership(groupId, BrassGroupType.AntGroup);
        if (!response || response.length === 0) {
          setGroupError('No group found with that name. Double-check the spelling or try a different name.');
          return;
        }
        // Add as a group attendee with cached members
        appendAttendees([
          {
            id: `group-${uuidv4()}`,
            inputName: groupId,
            matches: [],
            selectedEmployee: null,
            selectedExecutiveAssistant: null,
            status: 'exact',
            priority,
            resolvedEmail: `${groupId}@amazon.com`,
            isGroup: true,
            groupType: BrassGroupType.AntGroup,
            groupMembers: response
          }
        ]);
        setGroupText('');
        setGroupMode(false);
      } catch (error) {
        logger.error('Failed to add group', { groupId, error });
        setGroupError('Failed to verify group. Please try again.');
      } finally {
        setIsGroupLoading(false);
      }
    },
    [groupText, appendAttendees, existingEmails]
  );

  const principalsWithProxy = useMemo(
    () => new Set(resolvedAttendees.filter((a) => a.proxyForLogin).map((a) => a.proxyForLogin!.toLowerCase())),
    [resolvedAttendees]
  );

  const handleAttendeeUpdateWithResort = useCallback(
    (id: string, updates: Partial<ResolvedMatch>) => {
      onAttendeeUpdate(id, updates);
      if ('priority' in updates) onResort();
    },
    [onAttendeeUpdate, onResort]
  );

  // Close upload modal when processing completes successfully
  const wasProcessing = useRef(false);
  useEffect(() => {
    if (isProcessingDocument) {
      wasProcessing.current = true;
    } else if (wasProcessing.current) {
      wasProcessing.current = false;
      if (!documentError) setShowUploadLocal(false);
    }
  }, [isProcessingDocument, documentError]);

  const criticalAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Critical);
  const requiredAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Required);
  const optionalAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Optional);

  return (
    <>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        headerText="Attendee List"
        headerActions={
          <SpaceBetween direction="horizontal" size="xs">
            {!isReadOnly && (
              <span className="blue-button">
                <ButtonDropdown
                  variant="normal"
                  disabled={isLoadingSharedMeeting}
                  items={[
                    { id: 'upload-template', text: 'Upload from Template', iconName: 'upload' },
                    { id: 'paste-list', text: 'Paste Attendees', iconName: 'insert-row' },
                    { id: 'copy-from-meeting', text: 'Import from Meeting', iconName: 'copy' }
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'upload-template') setShowUploadLocal(true);
                    else if (detail.id === 'copy-from-meeting') setShowCopyFromMeeting(true);
                    else if (detail.id === 'paste-list') setShowPasteList(true);
                  }}
                >
                  Load Attendees
                </ButtonDropdown>
              </span>
            )}
            {onSaveMeeting && (
              <span className="blue-button">
                <Button variant="primary" onClick={onSaveMeeting} loading={isSaving} disabled={isSaving || resolvedAttendees.length === 0}>
                  Save
                </Button>
              </span>
            )}
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          {!isReadOnly && (
            <div className="search-toolbar-row">
              <div className="search-toolbar-left">
                <div className="search-bar-wrapper">
                  <button
                    type="button"
                    className="icon-toggle-button"
                    onClick={() => {
                      setGroupMode(!groupMode);
                      setGroupText('');
                      setGroupError('');
                      if (pasteMode) {
                        setPasteMode(false);
                        setPasteText('');
                        setPasteError('');
                      }
                    }}
                    title={groupMode ? 'Switch to search' : 'Add Email List'}
                    aria-label={groupMode ? 'Switch to search' : 'Add Email List'}
                  >
                    {groupMode ? <Icon name="search" size="medium" /> : <FontAwesomeIcon icon={faList} style={{ width: '20px', height: '24px' }} />}
                  </button>
                  <button
                    type="button"
                    className="icon-toggle-button"
                    onClick={() => {
                      setPasteMode(!pasteMode);
                      setPasteText('');
                      setPasteError('');
                      if (groupMode) {
                        setGroupMode(false);
                        setGroupText('');
                        setGroupError('');
                      }
                    }}
                    title={pasteMode ? 'Switch to search' : 'Bulk Add Attendees'}
                    aria-label={pasteMode ? 'Switch to search' : 'Bulk Add Attendees'}
                  >
                    <Icon name={pasteMode ? 'search' : 'insert-row'} size="medium" />
                  </button>
                  {pasteMode ? (
                    <div className="search-input-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Textarea
                            value={pasteText}
                            onChange={({ detail }) => setPasteText(detail.value)}
                            placeholder={PASTE_ATTENDEES_PLACEHOLDER}
                            rows={3}
                            disabled={isPasteLoading}
                          />
                        </div>
                        <ButtonDropdown
                          variant="normal"
                          disabled={!pasteText.trim() || isPasteLoading}
                          loading={isPasteLoading}
                          items={[
                            { id: AttendeePriority.Critical, text: 'Add as Critical' },
                            { id: AttendeePriority.Required, text: 'Add as Required' },
                            { id: AttendeePriority.Optional, text: 'Add as Optional' }
                          ]}
                          onItemClick={({ detail }) => handleInlinePasteSubmit(detail.id as AttendeePriority)}
                        >
                          Add
                        </ButtonDropdown>
                      </div>
                      {pasteError && (
                        <Alert statusIconAriaLabel="Error" type="error">
                          {pasteError}
                        </Alert>
                      )}
                    </div>
                  ) : groupMode ? (
                    <div className="search-input-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            value={groupText}
                            onChange={({ detail }) => {
                              setGroupText(detail.value);
                              setGroupError('');
                            }}
                            placeholder="Enter email list name..."
                            disabled={isGroupLoading}
                          />
                        </div>
                        <ButtonDropdown
                          variant="normal"
                          disabled={!groupText.trim() || isGroupLoading}
                          loading={isGroupLoading}
                          items={[
                            { id: AttendeePriority.Critical, text: 'Add as Critical' },
                            { id: AttendeePriority.Required, text: 'Add as Required' },
                            { id: AttendeePriority.Optional, text: 'Add as Optional' }
                          ]}
                          onItemClick={({ detail }) => handleAddGroup(detail.id as AttendeePriority)}
                        >
                          Add
                        </ButtonDropdown>
                      </div>
                      {groupError && (
                        <Alert statusIconAriaLabel="Error" type="error">
                          {groupError}
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <div className="search-input-content">
                      <EmployeeSearchBar onAddAttendee={onAddAttendee} existingEmails={existingEmails} />
                    </div>
                  )}
                </div>
                <Select
                  ariaLabel="Sort attendees by"
                  selectedOption={{ value: sortBy, label: `Sort By: ${SORT_LABELS[sortBy]}` }}
                  onChange={({ detail }) => onSortByChange(detail.selectedOption.value as SortOption)}
                  options={[
                    { value: 'priority', label: 'Priority' },
                    { value: 'level', label: 'Level' },
                    { value: 'name', label: 'Name' }
                  ]}
                />
                <CopyDropdown resolvedAttendees={resolvedAttendees} onMultiBadgerClick={onMultiBadgerClick} onDownloadCsv={onDownloadCsv} />
              </div>
            </div>
          )}

          {resolvedAttendees.length > 0 && (
            <SpaceBetween direction="vertical" size="xs">
              <Box padding={{ vertical: 'xxs' }}>
                <div className="attendees-header-wrapper">
                  <Grid gridDefinition={[{ colspan: 6 }, { colspan: 5 }, { colspan: 1 }]}>
                    <Box>
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <Box fontSize="heading-m" fontWeight="bold" textAlign="left">
                          Attendees ({resolvedAttendees.length})
                          {resolvedAttendees.length > 0 && (
                            <Box display="inline" fontSize="body-s" color="text-body-secondary" fontWeight="normal" margin={{ left: 'xs' }}>
                              {' '}
                              ({criticalAttendees.length > 0 && `${criticalAttendees.length} critical`}
                              {criticalAttendees.length > 0 && (requiredAttendees.length > 0 || optionalAttendees.length > 0) && ', '}
                              {requiredAttendees.length > 0 && `${requiredAttendees.length} required`}
                              {requiredAttendees.length > 0 && optionalAttendees.length > 0 && ', '}
                              {optionalAttendees.length > 0 && `${optionalAttendees.length} optional`})
                            </Box>
                          )}
                        </Box>
                      </SpaceBetween>
                    </Box>
                    <div className="attendee-column attendee-column--ea">
                      <Box fontSize="heading-m" fontWeight="bold">
                        Executive Assistants ({executiveAssistants.length})
                      </Box>
                    </div>
                    <div />
                  </Grid>
                </div>
              </Box>

              {isProcessingDocument || isLoadingSharedMeeting ? (
                <Alert statusIconAriaLabel="Loading" type="info" header={isLoadingSharedMeeting ? 'Loading Shared Meeting' : 'Document Processing'}>
                  <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Spinner size="normal" />
                    <span>
                      {isLoadingSharedMeeting
                        ? sharedMeetingProgress || 'Loading shared meeting attendees...'
                        : 'Processing document and resolving attendees...'}
                    </span>
                  </SpaceBetween>
                </Alert>
              ) : (
                <>
                  {duplicateEmails.size > 0 && (
                    <Alert statusIconAriaLabel="Warning" type="warning" header="Duplicate Email Alert">
                      Some attendees have duplicate email addresses. They will only be invited once.
                    </Alert>
                  )}
                  <div className="attendees-list">
                    {resolvedAttendees.map((attendee) => (
                      <AttendeeCard
                        key={attendee.id}
                        attendee={attendee}
                        onUpdate={handleAttendeeUpdateWithResort}
                        onRemove={onAttendeeRemove}
                        isDuplicate={attendee.resolvedEmail ? duplicateEmails.has(attendee.resolvedEmail.toLowerCase()) : false}
                        existingEmails={existingEmails}
                        onToggleFavorite={onToggleFavorite}
                        onAddProxy={onAddProxy}
                        onAddAttendee={onAddAttendee}
                        hasProxy={principalsWithProxy.has(attendee.selectedEmployee?.login_name?.toLowerCase() ?? '')}
                        isReadOnly={isReadOnly}
                        onEaOverride={onEaOverride}
                        onEaRevert={onEaRevert}
                      />
                    ))}
                  </div>
                </>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </ExpandableSection>

      {(showUploadModal || showUploadLocal) && (
        <UploadTemplateContainer
          visible={showUploadModal || showUploadLocal}
          onClose={() => {
            onHideUploadModal();
            setShowUploadLocal(false);
          }}
          isProcessingDocument={isProcessingDocument}
          documentError={documentError}
          onFileSelected={onFileSelected}
          onFormattedContentExtracted={onFormattedContentExtracted}
        />
      )}

      <ImportFromMeetingModal
        visible={showCopyFromMeeting}
        onDismiss={() => setShowCopyFromMeeting(false)}
        recentMeetings={recentMeetings}
        resolvedAttendees={resolvedAttendees}
        onImportComplete={(attendees) => {
          setShowCopyFromMeeting(false);
          if (attendees.length > 0) {
            loadAttendeesInBatches(attendees, onAddAttendee).catch((error) => {
              logger.error('Failed to load imported attendees', { error });
            });
          }
        }}
      />

      <PasteListModal visible={showPasteList} onDismiss={() => setShowPasteList(false)} onSubmit={resolvePastedNames} />
    </>
  );
};
